import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import Papa from "papaparse";
import { IconArrowLeft, IconUpload, IconDownload, IconCheck, IconAlertCircle } from "@tabler/icons-react";
import ApiClient from "../../helpers/ApiClient";

const MenuWithInventoryImportPage = () => {
  const navigate = useNavigate();
  const [csvData, setCsvData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileSelected, setFileSelected] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileSelected(file.name);
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          setCsvData(results.data);
        },
        error: (err) => {
          toast.error("Error parsing CSV file");
          console.error(err);
        }
      });
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await ApiClient.get("/menu-items/export-with-inventory");
      let dataToExport = res.data;
      if (!dataToExport || dataToExport.length === 0) {
        dataToExport = [
          {
            menu_title: "Chicken Burger",
            category: "Fast Food",
            description: "Delicious chicken burger",
            item_type: "Base",
            item_name: "",
            price: "150",
            ingredient_name: "Chicken Breast",
            ingredient_unit: "kg",
            ingredient_qty: "0.2",
            ingredient_category: "Meat",
            inventory_stock_qty: "50"
          }
        ];
      }
      
      const csv = Papa.unparse(dataToExport);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute("download", "advanced_menu_inventory_export.csv");
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      toast.error("Failed to export data");
      console.error(e);
    }
  };

  const handleImport = async () => {
    if (csvData.length === 0) {
      toast.error("No data to import");
      return;
    }

    setLoading(true);
    try {
      const response = await ApiClient.post("/menu-items/bulk-with-inventory", csvData);
      toast.success(response.data.message || "Imported successfully!");
      navigate("/dashboard/settings/menu-items");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to import menu items");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/dashboard/settings/menu-items"
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <IconArrowLeft className="w-5 h-5" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-800">Advanced Bulk Import (Menu + Inventory)</h1>
        </div>
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
        >
          <IconDownload className="w-4 h-4" />
          Download Template
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-8 flex flex-col items-center justify-center border-b border-gray-100">
          <div className="w-16 h-16 bg-purple-50 text-purple-600 rounded-full flex items-center justify-center mb-4">
            <IconUpload className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-1">Upload CSV File</h3>
          <p className="text-sm text-gray-500 mb-6 text-center max-w-sm">
            Upload your advanced CSV file. Make sure to follow the template structure carefully.
          </p>
          
          <label className="relative cursor-pointer">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className="hidden"
            />
            <span className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium flex items-center gap-2">
              {fileSelected || "Select CSV File"}
            </span>
          </label>
        </div>

        {csvData.length > 0 && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">
                Preview ({csvData.length} rows)
              </h4>
              <button
                onClick={handleImport}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center gap-2 disabled:opacity-50"
              >
                {loading ? "Importing..." : (
                  <>
                    <IconCheck className="w-4 h-4" />
                    Confirm Import
                  </>
                )}
              </button>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm text-left text-gray-500">
                <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                  <tr>
                    <th className="px-4 py-3">Menu Title</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Price</th>
                    <th className="px-4 py-3">Ingredient Name</th>
                    <th className="px-4 py-3">Ing Category</th>
                    <th className="px-4 py-3">Ing Qty (Recipe)</th>
                    <th className="px-4 py-3">Stock Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csvData.slice(0, 10).map((row, index) => (
                    <tr key={index} className="bg-white">
                      <td className="px-4 py-3 font-medium text-gray-900">{row.menu_title}</td>
                      <td className="px-4 py-3">{row.item_type}</td>
                      <td className="px-4 py-3">{row.item_name}</td>
                      <td className="px-4 py-3">{row.price}</td>
                      <td className="px-4 py-3">{row.ingredient_name}</td>
                      <td className="px-4 py-3">{row.ingredient_category || row.inventory_category || "Category"}</td>
                      <td className="px-4 py-3">{row.ingredient_qty} {row.ingredient_unit}</td>
                      <td className="px-4 py-3">{row.inventory_stock_qty || row.stock_quantity || row.stock_qty || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvData.length > 10 && (
              <p className="text-xs text-gray-400 mt-2 text-center">
                Showing first 10 rows...
              </p>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 text-amber-800">
        <IconAlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-semibold mb-1">Important Instructions:</p>
          <ul className="list-disc list-inside space-y-1 text-amber-700/90">
            <li>Ensure the column headers match the template EXACTLY.</li>
            <li><strong>Item Type</strong> must be one of: <code>Base</code>, <code>Variant</code>, or <code>Addon</code>.</li>
            <li><strong>Ingredient Name</strong>: The inventory item used to prepare this dish. If it doesn't exist yet, it will be automatically created in your inventory.</li>
            <li><strong>Ingredient Category</strong>: The category of this inventory item (e.g., Meat, Dairy, Bakery, Vegetables). Automatically registered into Inventory Categories.</li>
            <li><strong>Ing Qty (Recipe)</strong>: The amount of this ingredient consumed from inventory each time 1 serving of this menu item is ordered.</li>
            <li><strong>Inventory Stock Qty</strong>: (Optional) Initial stock quantity of this ingredient added to your inventory (defaults to 0 if empty).</li>
            <li>To assign multiple ingredients to the same item, just create another row with the same Menu Title and Item Type.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MenuWithInventoryImportPage;
